/**
 * ==========================================================================
 *  The shared material and geometry cache every builder draws out of.
 *
 *  Split out of `World` so that the individual builders - vehicles, people,
 *  tents, the muurikka - can be modules of their own rather than methods on a
 *  four-thousand-line class. They all need the same three things and nothing
 *  else of World's: a material for a `MaterialKey`, a unit box whose UVs are
 *  already scaled to the size it will be drawn at, and the depth ordering for
 *  surfaces that share a plane.
 *
 *  The caching is the whole point. The parking lot alone is 41 similar cars,
 *  and a level built without these maps would allocate a texture, a material
 *  and a geometry per bay.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { RENDER } from '../config/gameplay';
import { SURFACE_FINISH } from '../config/surfaces';
import { PALETTE, buildSurfaces, type Surface } from './surfaces';
import { PLATE_SIZE } from './decals';
import { boxCenter, boxSize, type Box, type MaterialKey } from '../world/Level';
import type { Vec3 } from '../utils/math';

export class SceneAssets {
  /** Keyed by material *and* depth level - see `material()`. */
  private readonly materials = new Map<string, THREE.Material>();
  /** Box and plane geometries with world-scaled UVs, keyed by material and size. */
  private readonly tiled = new Map<string, THREE.BufferGeometry>();
  /** Skin and clothes for the figures. Not level data - see `src/render/People.ts`. */
  private readonly figureMaterials = new Map<number, THREE.Material>();
  private readonly surfaces: Partial<Record<MaterialKey, Surface>>;

  readonly boxGeometry = new THREE.BoxGeometry(1, 1, 1);
  readonly planeGeometry = new THREE.PlaneGeometry(1, 1);
  /** One plane, shared by every registration plate on every vehicle. */
  readonly plateGeometry = new THREE.PlaneGeometry(PLATE_SIZE.width, PLATE_SIZE.height);

  /**
   * `anisotropy` is the renderer's maximum. Passed in rather than read here
   * because nothing in this file may own a renderer - it draws the tiles at
   * construction and hands them out, and is handed the one number that needs
   * to come off the canvas.
   */
  constructor(anisotropy = 1) {
    this.surfaces = buildSurfaces(anisotropy);
  }

  /** The drawn tile for a key, or undefined for the keys drawn as flat colour. */
  surface(key: MaterialKey): Surface | undefined {
    return this.surfaces[key];
  }

  /**
   * `depthLevel` is how far back this surface is drawn against another one on
   * the same plane: 0 is the front, and every level past it costs one clone of
   * the material with a depth bias on it. There are only ever a handful, and
   * they share their texture with the original.
   */
  material(key: MaterialKey, depthLevel = 0): THREE.Material {
    const id = depthLevel === 0 ? key : `${key}|${depthLevel}`;
    let material = this.materials.get(id);
    if (!material) {
      if (depthLevel !== 0) {
        material = this.material(key).clone();
        material.polygonOffset = true;
        // Units only. A slope-scaled factor would be metres deep at a grazing
        // angle, which is enough to reorder things that are genuinely apart.
        material.polygonOffsetFactor = 0;
        material.polygonOffsetUnits = depthLevel * RENDER.coplanar.bias;
        this.materials.set(id, material);
        return material;
      }
      const surface = this.surfaces[key];
      const finish = SURFACE_FINISH[key];
      if (key === 'screen' || key === 'ember') {
        // A monitor is a light source, not a lit surface - and neither is the
        // fire showing round the edge of the kamiina's hatch.
        material = new THREE.MeshBasicMaterial({ color: PALETTE[key] });
      } else if (surface && finish) {
        material = new THREE.MeshPhongMaterial({
          color: 0xffffff,
          map: surface.map,
          ...finish,
          // Board joints and roof ribs already encode their relief in the tile.
          // Reusing that map avoids another texture allocation per surface.
          bumpMap: finish.bumpScale ? surface.map : null,
        });
      } else {
        material = new THREE.MeshLambertMaterial({
          // The tile is already drawn in the palette colour, so tinting it by
          // that colour again would darken every textured surface twice.
          color: surface ? 0xffffff : PALETTE[key],
          map: surface?.map,
        });
      }
      this.materials.set(id, material);
    }
    return material;
  }

  /** Flat colour for skin, cloth and hair, which have no tile of their own. */
  figureMaterial(color: number): THREE.Material {
    let material = this.figureMaterials.get(color);
    if (!material) {
      material = new THREE.MeshLambertMaterial({ color });
      this.figureMaterials.set(color, material);
    }
    return material;
  }

  /**
   * A cylinder's UVs already run once around and once up, so the tiling goes on
   * the texture rather than into the geometry. Clones share the canvas, so this
   * costs a wrapper object per silo, not a second image.
   */
  cylinderMaterial(key: MaterialKey, spec: { radius: number; height: number }): THREE.Material {
    const surface = this.surfaces[key];
    if (!surface) return this.material(key);
    const map = surface.map.clone();
    map.repeat.set(
      Math.max(1, (2 * Math.PI * spec.radius) / surface.worldSize),
      Math.max(1, spec.height / surface.worldSize),
    );
    const material = this.material(key).clone() as THREE.MeshLambertMaterial | THREE.MeshPhongMaterial;
    material.map = map;
    if (material instanceof THREE.MeshPhongMaterial && material.bumpMap) material.bumpMap = map;
    return material;
  }

  /**
   * A unit box whose UVs are scaled to the size the mesh will be drawn at, so
   * one tile of the texture covers `Surface.worldSize` metres of world however
   * big the box is.
   *
   * Cached by material and size: the parking lot alone is 41 similar cars.
   */
  boxGeometryFor(size: Vec3, key: MaterialKey): THREE.BufferGeometry {
    const surface = this.surfaces[key];
    if (!surface) return this.boxGeometry;

    const id = `${key}|${round(size.x)}|${round(size.y)}|${round(size.z)}`;
    const cached = this.tiled.get(id);
    if (cached) return cached;

    const geometry = this.boxGeometry.clone();
    const uv = geometry.getAttribute('uv') as THREE.BufferAttribute;
    // BoxGeometry lays its faces out +X, -X, +Y, -Y, +Z, -Z, four vertices
    // each, and each face's u/v run along the two axes it does not face down.
    const spans: [number, number][] = [
      [size.z, size.y],
      [size.z, size.y],
      [size.x, size.z],
      [size.x, size.z],
      [size.x, size.y],
      [size.x, size.y],
    ];
    for (let face = 0; face < 6; face++) {
      const [spanU, spanV] = spans[face];
      for (let corner = 0; corner < 4; corner++) {
        const at = face * 4 + corner;
        uv.setXY(
          at,
          (uv.getX(at) * spanU) / surface.worldSize,
          (uv.getY(at) * spanV) / surface.worldSize,
        );
      }
    }
    uv.needsUpdate = true;

    this.tiled.set(id, geometry);
    return geometry;
  }

  /** The floor equivalent of `boxGeometryFor`. */
  tiledPlane(width: number, depth: number, key: MaterialKey, surface: Surface): THREE.BufferGeometry {
    const id = `plane|${key}|${round(width)}|${round(depth)}`;
    const cached = this.tiled.get(id);
    if (cached) return cached;

    const geometry = this.planeGeometry.clone();
    const uv = geometry.getAttribute('uv') as THREE.BufferAttribute;
    for (let at = 0; at < uv.count; at++) {
      uv.setXY(
        at,
        (uv.getX(at) * width) / surface.worldSize,
        (uv.getY(at) * depth) / surface.worldSize,
      );
    }
    uv.needsUpdate = true;

    this.tiled.set(id, geometry);
    return geometry;
  }

  /**
   * One box, placed and named. Not added to any scene: the caller owns where
   * it goes, which for a door's leaf or a tent's panel is a group rather than
   * the scene root.
   */
  box(box: Box, key: MaterialKey, name: string, depthLevel = 0): THREE.Mesh {
    const size = boxSize(box);
    const center = boxCenter(box);
    const mesh = new THREE.Mesh(this.boxGeometryFor(size, key), this.material(key, depthLevel));
    mesh.scale.set(Math.max(size.x, 1e-3), Math.max(size.y, 1e-3), Math.max(size.z, 1e-3));
    mesh.position.set(center.x, center.y, center.z);
    mesh.name = name;
    return mesh;
  }

  /**
   * Rescales an existing geometry's UVs in place, for the shapes that are not
   * boxes and not floors: a tent's sloped panel, a camper's screen.
   */
  scaleUv(geometry: THREE.BufferGeometry, key: MaterialKey, spanU: number, spanV: number): void {
    const surface = this.surfaces[key];
    if (!surface) return;
    const uv = geometry.getAttribute('uv') as THREE.BufferAttribute;
    for (let at = 0; at < uv.count; at++) {
      uv.setXY(
        at,
        (uv.getX(at) * spanU) / surface.worldSize,
        (uv.getY(at) * spanV) / surface.worldSize,
      );
    }
    uv.needsUpdate = true;
  }
}

/**
 * Five centimetres. Two boxes within that of each other share a geometry, which
 * is what makes the cache hit at all across a lot of hand-placed props.
 */
function round(value: number): number {
  return Math.round(value * 20) / 20;
}
